
import { Component, Vue } from 'vue-property-decorator'
import { FlatPagesRepositoryFetch } from './FlatPagesRepositoryFetch'
import type { FlatPage } from './schema'
import { MetaInfo } from 'vue-meta'
import SCHEMA_ORGANIZATION from '../../json/schemaOrganization.json'

@Component<FlatPageComponent>({
  httpHeaders: ({ $config }) => ({
    'Cache-Control': `max-age=${$config.nonPriceCacheTime}, public`,
    Grace: `${$config.nonPriceGraceTime}`,
    xkey: 'view:flatpage',
  }),
  middleware: ['httpHeaders'],
  head: function () {
    return this.head()
  },
  async asyncData(context) {
    const { error, route, $axios } = context
    try {
      const flatPageContent = await new FlatPagesRepositoryFetch($axios).get(
        route.path,
        route.params.lang || 'en-gb',
      )
      if ('location' in flatPageContent) {
        return context.redirect(301, flatPageContent.location)
      }
      flatPageContent.content = flatPageContent.content.replace(
        'loading="lazy"',
        'fetchpriority="high" decoding="sync"',
      )
      if (process.server) {
        const { res } = context
        res.setHeader('xkey', `view:flatpage flatpage:${route.path}`)
      }
      return { flatPageContent }
    } catch (e: any) {
      const statusCode =
        e.response && e.response.status
          ? e.response.status
          : e.response && e.response.statusCode
            ? e.response.statusCode
            : undefined
      // @ts-ignore
      if (statusCode !== 404) {
        if (typeof context.$sentry !== 'undefined') {
          // @ts-ignore
          context.$sentry.captureException(e)
        }
        return error({ statusCode })
      }
      context.res.statusCode = 404
    }
    return {}
  },
  validate({ route }) {
    const isAjax = route.path.startsWith('/_')
    const hasFileExtension = /\.\w+$/.test(route.path)
    return !isAjax && !hasFileExtension
  },
})
export default class FlatPageComponent extends Vue {
  flatPageContent: FlatPage | null = null

  head(): MetaInfo {
    return {
      title: `${this.flatPageContent?.title} | Pitchup.com`,
      meta: [this.titleMetaData, this.descriptionMetaData],
      bodyAttrs: {
        class: 'promo-page',
      },
      script: [
        this.newsArticleScript,
        this.breadcrumbList,
        this.schemaOrganization,
      ],
    }
  }

  mounted() {
    // temporary #4944. Move article date below h1
    const titleElement = document.querySelector(
      '.flatpagedata h1:first-of-type',
    )
    const publishedDate = document.querySelector('.published-date')
    if (titleElement && publishedDate) {
      titleElement.after(publishedDate)
    }
  }

  get schemaOrganization() {
    return {
      type: 'application/ld+json',
      body: true,
      json: SCHEMA_ORGANIZATION,
    }
  }

  get newsArticleScript() {
    const json = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': this.articleAbsolutePath,
      },
      headline: this.title,
      description: this.description,
      author: this.author,
      publisher: this.publisher,
    }
    if (this.flatPageContent?.dateFrom)
      json['datePublished'] = this.flatPageContent.dateFrom
    if (this.firstPhoto) json['image'] = this.firstPhoto
    return {
      type: 'application/ld+json',
      body: true,
      json,
    }
  }

  get articleAbsolutePath() {
    return `https://${this.$config.public.envUrl}${this.$route.path}`
  }

  get breadcrumbList() {
    const prefix = this.$route.params.lang ? '/' + this.$route.params.lang : ''
    const json = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': `https://${this.$config.public.envUrl}${prefix}/`,
            name: this.$i18n.t('Home') as string,
          },
        },
        ...(this.flatPageContent?.breadcrumbs?.map((breadcrumb, index) => ({
          '@type': 'ListItem',
          position: index + 2,
          item: {
            '@id': `https://${this.$config.public.envUrl}${prefix}${breadcrumb.url}`,
            name: breadcrumb.title,
          },
        })) || []),
        {
          '@type': 'ListItem',
          position: (this.flatPageContent?.breadcrumbs?.length || 0) + 2,
          item: {
            '@id': this.articleAbsolutePath,
            name: this.title,
          },
        },
      ],
    }
    return {
      type: 'application/ld+json',
      body: true,
      json,
    }
  }

  get title() {
    return this.flatPageContent?.title || ''
  }

  get description() {
    return this.flatPageContent?.description || ''
  }

  get firstPhoto() {
    return this.flatPageContent?.firstPhoto
      ? this.flatPageContent.firstPhoto
      : null
  }

  get author() {
    return {
      '@type': 'Organization',
      name: 'Pitchup.com',
    }
  }

  get publisher() {
    return {
      '@type': 'Organization',
      name: 'Pitchup.com',
      alternateName: 'Pitchup',
      logo: {
        '@type': 'ImageObject',
        url: `https://${this.$config.public.envUrl}/app-icons/pitchup-icon-1024x1024.png`,
      },
    }
  }

  get descriptionMetaData() {
    return {
      hid: 'description',
      name: 'description',
      property: 'og:description',
      content: this.description,
    }
  }

  get titleMetaData() {
    return {
      hid: 'og:title',
      property: 'og:title',
      content: this.title,
    }
  }
}
